// Agregações derivadas dos RDOs reais de uma obra (tabela `rdos` no Supabase).
// Fonte única para StatsCards, gráficos, galerias e modais do painel da obra.
import { listRDOs, getRDOs } from "@/services/rdoStorageSupabase";
import type { RDO } from "@/services/rdoStorageService";

export interface ObraStats {
  /** RDOs da obra, ordenados por data (mais recente primeiro) */
  rdos: RDO[];
  totalRdos: number;
  totalOcorrencias: number;
  totalFotos: number;
  totalVideos: number;
  totalAnexos: number;
  pendentesAprovacao: number;
  /** Pessoas no RDO mais recente */
  maoDeObraUltimo: number;
  /** Média de pessoas/dia nos últimos 10 RDOs */
  mediaMaoDeObra10: number;
  /** Equipamentos no RDO mais recente */
  equipamentosUltimo: number;
}

export function workforceCount(rdo: RDO): number {
  return (rdo.workforce ?? []).reduce((sum, w) => sum + (w.quantity ?? 1), 0);
}

export function equipmentCount(rdo: RDO): number {
  return (rdo.equipment ?? []).length;
}

export function isPendingApproval(rdo: RDO): boolean {
  // Rascunho ainda está em edição; só conta como pendente o que foi enviado
  // para revisão (in_review/review/signed) e ainda não foi aprovado.
  return !rdo.approved && rdo.status !== "approved" && rdo.status !== "draft";
}

function aggregate(rdos: RDO[]): ObraStats {
  const last = rdos[0];
  const last10 = rdos.slice(0, 10);
  const media =
    last10.length > 0
      ? Math.round(last10.reduce((s, r) => s + workforceCount(r), 0) / last10.length)
      : 0;
  return {
    rdos,
    totalRdos: rdos.length,
    totalOcorrencias: rdos.reduce((s, r) => s + (r.occurrences?.length ?? 0), 0),
    totalFotos: rdos.reduce((s, r) => s + (r.photos?.length ?? 0), 0),
    totalVideos: rdos.reduce((s, r) => s + (r.videos?.length ?? 0), 0),
    totalAnexos: rdos.reduce((s, r) => s + (r.attachments?.length ?? 0), 0),
    pendentesAprovacao: rdos.filter(isPendingApproval).length,
    maoDeObraUltimo: last ? workforceCount(last) : 0,
    mediaMaoDeObra10: media,
    equipamentosUltimo: last ? equipmentCount(last) : 0,
  };
}

/** Estatísticas de uma obra específica */
export async function getObraStats(obraId: string): Promise<ObraStats> {
  const rdos = await listRDOs(obraId);
  return aggregate(rdos);
}

/** Estatísticas agregadas de todas as obras do usuário */
export async function getGlobalStats(): Promise<ObraStats> {
  const rdos = await getRDOs();
  return aggregate(rdos);
}

/** Série diária (data asc) de pessoas em obra — para gráficos de histograma */
export function workforceSeries(rdos: RDO[]): Array<{ date: string; pessoas: number }> {
  return rdos
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((r) => ({ date: r.date, pessoas: workforceCount(r) }));
}
